/**
 * SystemFactory - Centralizes game system creation
 * 
 * Extracted from GameScene.create() to reduce complexity and enable testing.
 * Systems are created in phases based on their dependencies.
 */

import Phaser from 'phaser';
import { StatsTracker } from './StatsTracker';
import { PlayerListUI } from '../ui/PlayerListUI';
import { SessionManager } from './SessionManager';
import { SpectatorSystem } from './SpectatorSystem';
import { DeathReplaySystem } from './DeathReplaySystem';

// ============================================================================
// Phase A: Independent Systems (no external dependencies)
// ============================================================================

/**
 * Systems that have no dependencies on Phaser, Player, or network state.
 * Safe to create at any point during initialization.
 */
export interface IndependentSystems {
    statsTracker: StatsTracker;
    playerListUI: PlayerListUI;
    sessionManager: SessionManager;
}

/**
 * Create all independent systems.
 * These can be created before Player or other game objects exist.
 */
export function createIndependentSystems(): IndependentSystems {
    return {
        statsTracker: new StatsTracker(),
        playerListUI: new PlayerListUI(),
        sessionManager: new SessionManager(),
    };
}

// ============================================================================
// Phase B: Visual Systems (need scene reference only)
// ============================================================================

/**
 * Systems that need a Phaser.Scene but no other dependencies.
 */
export interface VisualSystems {
    spectatorSystem: SpectatorSystem;
    deathReplaySystem: DeathReplaySystem;
}

/**
 * Create visual systems that require scene reference.
 * @param scene - The Phaser scene to attach systems to
 */
export function createVisualSystems(scene: Phaser.Scene): VisualSystems {
    return {
        spectatorSystem: new SpectatorSystem(scene),
        deathReplaySystem: new DeathReplaySystem(scene),
    };
}

// ============================================================================
// Phase C: Gameplay Systems (complex interdependencies)
// ============================================================================

/**
 * Gameplay systems with complex interdependencies.
 * These are NOT created via factory due to:
 * - Circular dependencies requiring post-creation wiring
 * - Many constructor parameters from different sources
 * - Callbacks that need to be wired to other systems
 * 
 * Dependencies documented for reference:
 * 
 * FartSystem:
 *   - scene, player, fartsGroup, statsTracker
 *   - Post-creation: setOnBroadcast(), setPlayers(), setMazeData()
 * 
 * DamageSystem:
 *   - scene, player, fartsGroup, mazeData, statsTracker, deathReplaySystem, isMultiplayer
 *   - Post-creation: setMultiplayerSync()
 * 
 * PickupSystem:
 *   - scene, player, inventory, mazeData, spawnPoints, config, isMultiplayer
 *   - Post-creation: spawnInitialPickups(), onCollect()
 */
export interface GameplaySystems {
    fartSystem: unknown;      // FartSystem - complex wiring
    damageSystem: unknown;    // DamageSystem - complex wiring
    pickupSystem: unknown;    // PickupSystem - complex wiring
}

// Note: createGameplaySystems() intentionally not implemented.
// The interdependencies are best handled inline in GameScene.create().
